import { MetadataProvider } from "@/components/metadata/MetadataProvider";
import Providers from "@/components/providers";
import { GA_TRACKING_ID, VITE_ENV } from "@/configs/env.config";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import LoadingOverLay from "./components/loading-overlay";
import { generateRoutes } from "./router/generateRoutes";
import { ProtectedRoute } from "./router/ProtectedRoute";

// Configure NProgress
NProgress.configure({ showSpinner: true });

// Progress bar component - must be inside BrowserRouter
function ProgressBar() {
  const location = useLocation();

  useEffect(() => {
    NProgress.start();
    const timer = setTimeout(() => {
      NProgress.done();
    }, 200);

    return () => {
      clearTimeout(timer);
      NProgress.done();
    };
  }, [location.pathname]);

  return null;
}

// Google Analytics setup
function GoogleAnalytics() {
  useEffect(() => {
    if (VITE_ENV !== "development" && GA_TRACKING_ID) {
      // Load gtag script
      const script1 = document.createElement("script");
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
      script1.async = true;
      document.head.appendChild(script1);

      // Initialize gtag
      const script2 = document.createElement("script");
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_TRACKING_ID}', {
          page_path: window.location.pathname,
        });
      `;
      document.head.appendChild(script2);

      return () => {
        if (document.head.contains(script1)) document.head.removeChild(script1);
        if (document.head.contains(script2)) document.head.removeChild(script2);
      };
    }
  }, []);

  return null;
}

function App() {
  const routes = generateRoutes();

  return (
    <Providers>
      <GoogleAnalytics />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MetadataProvider>
          <ProgressBar />
          <Suspense fallback={<LoadingOverLay />}>
            <Routes>
              {routes.map((route) => {
                const isPublicRoute = route.path === "/" || route.path === "/login";
                const element = isPublicRoute ? route.element : <ProtectedRoute>{route.element}</ProtectedRoute>;

                return <Route key={route.path} path={route.path} element={element} errorElement={route.errorElement} />;
              })}
            </Routes>
          </Suspense>
        </MetadataProvider>
      </BrowserRouter>
      <Toaster richColors position="top-center" visibleToasts={5} />
    </Providers>
  );
}

export default App;
